import React from 'react';
import { browserHistory } from 'react-router';
import {
  LayoutDashboard,
  UserPlus, CheckCircle, Clock, ThumbsUp,
  FileText, Hourglass, BadgeCheck, Building2,
  XCircle, Activity, BarChart2,
  PlusCircle, Eye,
  Users, MapPin, Network, Wrench,
  ChevronsLeft, ChevronsRight,
} from './Icons';
import userdata from '../config/jwt_token';
import zapsLogo from '../images/vopr.png';

/* ─── Permission helper (mirrors HomeScreen logic) ─── */
function computePermissions() {
  if (!userdata) return {};
  const role = userdata.role || '';
  const p = {
    disableVendorOnboard: true, disableVerifiedVendors: true,
    disablePendingVendor: true, disablePaymentRequest: true,
    disablePaymentPending: true, disablePaymentApproved: true,
    disablePaymentApprove: true, disableApproveVendor: true,
    disablePaymentGeneratedBUF: true, disablePaymentStatus: true,
    disableAddCostCenter: true, disableViewCostCenter: true,
    disableAddUsers: true, disableBufRejectedPayments: true,
    disableCityManagement: true, disableBusinessUnitManagement: true,
    disableRecoverableAmountConfig: true,
  };
  if (role.indexOf('Central Finance') !== -1) {
    p.disableVerifiedVendors = p.disableApproveVendor = p.disablePaymentApproved =
    p.disablePaymentApprove = p.disablePaymentPending = p.disablePaymentStatus =
    p.disableAddCostCenter = p.disableBufRejectedPayments = p.disablePaymentGeneratedBUF =
    p.disableCityManagement = p.disableBusinessUnitManagement = p.disableRecoverableAmountConfig = false;
  }
  if (role === 'City Finance') {
    p.disableVendorOnboard = p.disableVerifiedVendors = p.disablePendingVendor =
    p.disablePaymentRequest = p.disablePaymentApproved = p.disablePaymentPending =
    p.disablePaymentStatus = p.disableViewCostCenter = p.disablePaymentGeneratedBUF =
    p.disableBufRejectedPayments = p.disableCityManagement = p.disableBusinessUnitManagement =
    p.disableRecoverableAmountConfig = false;
  }
  if (role === 'City Head') {
    p.disableVerifiedVendors = p.disablePaymentApproved = p.disablePaymentApprove =
    p.disablePaymentPending = p.disablePaymentStatus = p.disablePaymentGeneratedBUF =
    p.disableBufRejectedPayments = p.disableViewCostCenter = false;
  }
  if (role === 'Vendor Approver L2') {
    p.disableVerifiedVendors = p.disableApproveVendor = false;
  }
  if (role === 'root') {
    Object.keys(p).forEach(k => { p[k] = false; });
  }
  if (role === 'Super Central Finance') {
    p.disableVerifiedVendors = p.disableApproveVendor = p.disablePaymentApproved =
    p.disablePaymentApprove = p.disablePaymentPending = p.disablePaymentStatus =
    p.disableAddCostCenter = p.disableBufRejectedPayments = p.disablePaymentGeneratedBUF =
    p.disableCityManagement = p.disableBusinessUnitManagement = p.disableRecoverableAmountConfig = false;
  }
  if (role === 'Payment Hold Operator') { p.disablePaymentApproved = false; }
  return p;
}

/* ─── Navigation config with Lucide icons ─── */
const NAV_GROUPS = [
  {
    id: 'home', label: null,
    items: [
      { label: 'Dashboard', path: '/home', Icon: LayoutDashboard, permKey: null },
    ],
  },
  {
    id: 'vendors', label: 'Vendors',
    items: [
      { label: 'Vendor Onboarding', path: '/vendor/onboard',  Icon: UserPlus,     permKey: 'disableVendorOnboard'   },
      { label: 'Verified Vendors',  path: '/vendor/approved', Icon: CheckCircle,  permKey: 'disableVerifiedVendors' },
      { label: 'Pending Vendors',   path: '/vendor/pending',  Icon: Clock,        permKey: 'disablePendingVendor'   },
      { label: 'Approve Vendors',   path: '/vendor/approval', Icon: ThumbsUp,     permKey: 'disableApproveVendor'   },
    ],
  },
  {
    id: 'payments', label: 'Payments',
    items: [
      { label: 'Payment Request', path: '/initializePR',            Icon: FileText,   permKey: 'disablePaymentRequest'      },
      { label: 'Pending',         path: '/vendor/payment/pending',  Icon: Hourglass,  permKey: 'disablePaymentPending'      },
      { label: 'Approve',         path: '/vendor/payment/approve',  Icon: ThumbsUp,   permKey: 'disablePaymentApprove'      },
      { label: 'Processed',       path: '/vendor/payment/approved', Icon: BadgeCheck, permKey: 'disablePaymentApproved'     },
      { label: 'Generated BUF',   path: '/payment/generated_buf',   Icon: Building2,  permKey: 'disablePaymentGeneratedBUF' },
      { label: 'BUF Rejected',    path: '/payment/buf-rejected',    Icon: XCircle,    permKey: 'disableBufRejectedPayments' },
      { label: 'Payment Status',  path: '/payment/status',          Icon: Activity,   permKey: 'disablePaymentStatus'       },
    ],
  },
  {
    id: 'cost-centers', label: 'Cost Centers',
    items: [
      { label: 'Add Cost Center',  path: '/cost-center/add',  Icon: PlusCircle, permKey: 'disableAddCostCenter'  },
      { label: 'View Cost Center', path: '/cost-center/view', Icon: Eye,        permKey: 'disableViewCostCenter' },
    ],
  },
  {
    id: 'reports', label: 'Reports',
    items: [
      { label: 'Reports Hub', path: '/payment/reports', Icon: BarChart2, permKey: null },
    ],
  },
  {
    id: 'admin', label: 'Admin',
    items: [
      { label: 'Add Users',          path: '/users',                     Icon: Users,   permKey: 'disableAddUsers'               },
      { label: 'City Management',    path: '/cities',                    Icon: MapPin,  permKey: 'disableCityManagement'         },
      { label: 'BU Management',      path: '/business-units',            Icon: Network, permKey: 'disableBusinessUnitManagement' },
      { label: 'Recoverable Config', path: '/recoverable-amount-config', Icon: Wrench,  permKey: 'disableRecoverableAmountConfig'},
    ],
  },
];

/* ─── Sidebar Component ─── */
class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    const collapsed = localStorage.getItem('sidebar_collapsed') === 'true';
    this.state = { collapsed, permissions: computePermissions() };
  }

  componentDidMount() {
    this._applyBodyClass(this.state.collapsed);
  }

  /* Public method — Header hamburger calls this via ref */
  toggle() {
    const collapsed = !this.state.collapsed;
    localStorage.setItem('sidebar_collapsed', String(collapsed));
    this._applyBodyClass(collapsed);
    this.setState({ collapsed });
    if (this.props.onToggle) this.props.onToggle(collapsed);
  }

  _applyBodyClass(collapsed) {
    document.body.classList.remove('sidebar-open', 'sidebar-closed', 'no-sidebar');
    document.body.classList.add(collapsed ? 'sidebar-closed' : 'sidebar-open');
  }

  _navigate(path, disabled) {
    if (disabled) return;
    window.location.href = path;
  }

  _isActive(path) {
    const cur = window.location.pathname;
    if (path === '/home') return cur === '/home';
    return cur === path || cur.startsWith(path + '/');
  }

  _renderItem(item, permissions) {
    const { Icon, label, path, permKey } = item;
    const disabled = permKey ? !!permissions[permKey] : false;
    const active   = this._isActive(path);
    const { collapsed } = this.state;

    let cls = 'zaps-nav-item';
    if (active)   cls += ' active';
    if (disabled) cls += ' disabled';

    return (
      <div
        key={path}
        className={cls}
        title={collapsed ? label : (disabled ? "You don't have access to this section" : '')}
        onClick={() => this._navigate(path, disabled)}
      >
        <Icon size={15} className="zaps-nav-item-icon" strokeWidth={active ? 2.5 : 1.8} />
        <span className="zaps-nav-item-label">{label}</span>
      </div>
    );
  }

  render() {
    const { collapsed, permissions } = this.state;
    const cls = `zaps-sidebar ${collapsed ? 'collapsed' : 'expanded'}`;

    return (
      <div className={cls}>

        {/* ── Brand ── */}
        <div className="zaps-sidebar-brand">
          <a href="/home" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <img
              src={zapsLogo}
              alt="ZAPS"
              style={{
                height: collapsed ? 32 : 36,
                width: 'auto',
                maxWidth: collapsed ? 40 : 120,
                objectFit: 'contain',
                filter: 'invert(1) brightness(2)',
                transition: 'all 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </a>
        </div>

        {/* ── Navigation ── */}
        <div className="zaps-sidebar-nav">
          {NAV_GROUPS.map(group => (
            <div key={group.id}>
              {group.label && !collapsed && (
                <div className="zaps-nav-section-label">{group.label}</div>
              )}
              {group.label && collapsed && (
                <div className="zaps-nav-divider" />
              )}
              {group.items.map(item => this._renderItem(item, permissions))}
            </div>
          ))}
        </div>

        {/* ── Collapse toggle ── */}
        <div
          className="zaps-sidebar-toggle"
          onClick={() => this.toggle()}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {!collapsed && <span style={{ fontSize: 12 }}>Collapse</span>}
          {collapsed
            ? <ChevronsRight size={16} strokeWidth={1.8} />
            : <ChevronsLeft  size={16} strokeWidth={1.8} />
          }
        </div>

      </div>
    );
  }
}

export default Sidebar;
export { computePermissions, NAV_GROUPS };



// WEBPACK FOOTER //
// ./src/components/Sidebar.js